import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { cacheImageToFirebase } from '../utils/cacheImage';
import { secureImageUrl } from '../utils/secureImageUrl';

function CacheImagesMigration() {
  const [status, setStatus] = useState('');
  const [logs, setLogs] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const navigate = useNavigate();

  const addLog = (message) => {
    setLogs(prev => [...prev, message]);
  };

  const runMigration = async () => {
    setIsRunning(true);
    setStatus('🚀 Migrazione cache immagini in corso...');
    setLogs([]);
    setProgress({ current: 0, total: 0 });

    try {
      addLog('📡 Recupero wishlists dal database...');
      
      let totalWishlists = 0;
      let totalProducts = 0;
      let cachedImages = 0;
      let skippedImages = 0;
      let failedImages = 0;

      const wishlistsSnapshot = await getDocs(collection(db, 'wishlists'));
      totalWishlists = wishlistsSnapshot.size;
      
      addLog(`📊 Trovate ${totalWishlists} wishlists da processare`);

      // First pass: count total products with external images
      let productsToCache = 0;
      for (const wishlistDoc of wishlistsSnapshot.docs) {
        const products = wishlistDoc.data().products || [];
        for (const product of products) {
          if (product.image && 
              !product.image.startsWith('data:') && 
              !product.image.includes('firebasestorage.googleapis.com')) {
            productsToCache++;
          }
        }
      }

      addLog(`🔍 Trovate ${productsToCache} immagini da cachare`);
      setProgress({ current: 0, total: productsToCache });

      let processedCount = 0;

      // Second pass: actually cache the images
      for (const wishlistDoc of wishlistsSnapshot.docs) {
        const wishlistData = wishlistDoc.data();
        const products = wishlistData.products || [];
        
        if (products.length === 0) continue;
        
        totalProducts += products.length;
        let modified = false;

        // Process each product
        const updatedProducts = await Promise.all(
          products.map(async (product) => {
            // Skip if no image or already cached
            if (!product.image) return product;
            
            if (product.image.startsWith('data:')) {
              skippedImages++;
              return product;
            }
            
            if (product.image.includes('firebasestorage.googleapis.com')) {
              addLog(`⏭️ Già in cache: ${product.title?.substring(0, 40)}...`);
              skippedImages++;
              return product;
            }

            // Cache the image
            try {
              processedCount++;
              setProgress({ current: processedCount, total: productsToCache });

              addLog(`📦 Caching: ${product.title?.substring(0, 40)}...`);
              addLog(`   FROM: ${product.image.substring(0, 60)}...`);

              const productId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              const cacheResult = await cacheImageToFirebase(product.image, productId);

              if (cacheResult.cached && cacheResult.url !== product.image) {
                addLog(`   TO: Firebase Storage ✅`);
                cachedImages++;
                modified = true;
                return { ...product, image: cacheResult.url };
              } else {
                // Caching failed - use fallback URL
                addLog(`   ⚠️ Caching failed: ${cacheResult.error || 'Unknown error'}`);
                addLog(`   📌 Using fallback: ${cacheResult.url.substring(0, 60)}...`);
                failedImages++;
                
                // IMPORTANT: Still mark as modified to save the fallback URL
                if (cacheResult.url !== product.image) {
                  modified = true;
                }
                return { ...product, image: cacheResult.url };
              }

            } catch (error) {
              addLog(`   ❌ Error: ${error.message}`);
              failedImages++;
              // IMPORTANT: Still mark as modified to save the HTTPS conversion
              const httpsUrl = secureImageUrl(product.image);
              if (httpsUrl !== product.image) {
                modified = true;
              }
              return { ...product, image: httpsUrl };
            }
          })
        );

        // Update wishlist if any products were modified
        if (modified) {
          await updateDoc(doc(db, 'wishlists', wishlistDoc.id), {
            products: updatedProducts
          });
          addLog(`✅ Aggiornata wishlist: ${wishlistData.name}\n`);
        }
      }

      addLog('');
      addLog('🎉 MIGRAZIONE CACHE IMMAGINI COMPLETATA!');
      addLog(`📊 Statistiche:`);
      addLog(`   - Wishlists totali: ${totalWishlists}`);
      addLog(`   - Prodotti totali: ${totalProducts}`);
      addLog(`   - Immagini cachate su Firebase: ${cachedImages}`);
      addLog(`   - Immagini già in cache: ${skippedImages}`);
      addLog(`   - Immagini fallite: ${failedImages}`);
      addLog('');
      addLog('✅ Tutte le immagini possibili sono ora su Firebase Storage!');
      addLog('🔄 Ricarica l\'app iOS per vedere le modifiche.');

      setStatus(`✅ Completata! ${cachedImages} immagini cachate su Firebase.`);

    } catch (error) {
      console.error('Errore migrazione:', error);
      addLog(`❌ Errore: ${error.message}`);
      setStatus('❌ Migrazione fallita. Vedi i log per dettagli.');
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div style={{
      maxWidth: '900px',
      margin: '50px auto',
      padding: '30px',
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
    }}>
      <h1 style={{ marginBottom: '10px' }}>🖼️ Cache Immagini su Firebase</h1>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Scarica tutte le immagini esterne e le carica su Firebase Storage per risolvere:
      </p>
      <ul style={{ color: '#666', marginBottom: '30px', paddingLeft: '20px' }}>
        <li>Problemi SSL/TLS dei siti esterni</li>
        <li>Link Amazon scaduti</li>
        <li>Errori CORS su iOS</li>
        <li>Immagini non disponibili</li>
      </ul>

      {progress.total > 0 && (
        <div style={{
          marginBottom: '20px',
          padding: '10px',
          background: '#f0f0f0',
          borderRadius: '6px'
        }}>
          <div style={{ marginBottom: '5px' }}>
            Progresso: {progress.current} / {progress.total} immagini
          </div>
          <div style={{
            width: '100%',
            height: '20px',
            background: '#e0e0e0',
            borderRadius: '10px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${(progress.current / progress.total) * 100}%`,
              height: '100%',
              background: '#007bff',
              transition: 'width 0.3s'
            }} />
          </div>
        </div>
      )}

      <button
        onClick={runMigration}
        disabled={isRunning}
        style={{
          background: isRunning ? '#ccc' : '#28a745',
          color: 'white',
          border: 'none',
          padding: '12px 24px',
          borderRadius: '6px',
          cursor: isRunning ? 'not-allowed' : 'pointer',
          fontSize: '16px',
          fontWeight: '600',
          marginRight: '10px'
        }}
      >
        {isRunning ? '⏳ Migrazione in corso...' : '🚀 Avvia Cache Immagini'}
      </button>

      <button
        onClick={() => navigate('/')}
        disabled={isRunning}
        style={{
          background: '#6c757d',
          color: 'white',
          border: 'none',
          padding: '12px 24px',
          borderRadius: '6px',
          cursor: isRunning ? 'not-allowed' : 'pointer',
          fontSize: '16px',
          fontWeight: '600'
        }}
      >
        Torna alla Home
      </button>

      {status && (
        <div style={{
          marginTop: '20px',
          padding: '15px',
          borderRadius: '6px',
          background: status.includes('✅') ? '#d4edda' : status.includes('❌') ? '#f8d7da' : '#e7f3ff',
          border: `1px solid ${status.includes('✅') ? '#c3e6cb' : status.includes('❌') ? '#f5c6cb' : '#b3d7ff'}`
        }}>
          {status}
        </div>
      )}

      {logs.length > 0 && (
        <div style={{
          marginTop: '20px',
          padding: '15px',
          background: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          maxHeight: '500px',
          overflowY: 'auto',
          fontFamily: 'Courier New, monospace',
          fontSize: '13px'
        }}>
          {logs.map((log, index) => (
            <div key={index}>{log}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CacheImagesMigration;
